import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { theme } from "../theme"; // Assuming you have a theme with color variants
import { rgba } from "polished";
import { Flex } from "./layout";

const IconStyle = styled(Flex)((props) => {
  const {
    size = "md",
    shape = "circle",
    variant = "filled",
    sx = {},
    src,
    color,
  } = props;

  let iconSize, iconPadding;
  switch (size) {
    case "sm":
      iconSize = "1.25rem";
      iconPadding = "0.25rem";
      break;
    case "lg":
      iconSize = "1.875rem";
      iconPadding = "0.25rem";
      break;
    default:
      iconSize = "1.5rem";
      iconPadding = "0.25rem";
  }

  // Determine background color based on the variant
  const backgroundColor = (() => {
    switch (variant) {
      case "filled":
        return src
          ? "transparent" // Keep it transparent if an image is present
          : rgba(theme.colors[color]?.light, 0.15) ||
              theme.colors.default.light;
      case "contained":
        return src
          ? "transparent" // Keep it transparent if an image is present
          : theme.colors[color]?.main || theme.colors.default.main; // Lightened background color for filled/contained
      case "outlined":
        return "transparent"; // Outlined variant has no background color
      default:
        return  "transparent";
    }
  })();

  const borderColor =
    variant === "outlined" ? theme.colors[color]?.main : "transparent";

  return {
    width: iconSize,
    minWidth: iconSize,
    height: iconSize,
    minHeight: iconSize,
    borderRadius: shape === "circle" ? "50%" : "0.5rem",
    backgroundColor,
    fontWeight: 500,
    alignItem: "center",
    justifyContent: "center",
    color:theme.colors[color]?.dark, // Adjust text color for contrast
    textTransform: "uppercase",
    border: variant === "outlined" ? `2px solid ${borderColor}` : "none", // Outlined variant
    padding: iconPadding,
    boxShadow:
      variant === "contained"
        ? `0 2px 4px ${rgba(theme.colors[color]?.main, 0.2)}`
        : "none", // Optional shadow for contained variant
    ...sx,
  };
});

export const Icon = ({
  size,
  shape,
  src,
  icon,
  initials,
  color,
  variant,
  className,
  children,
  sx,
}) => {
  return (
    <IconStyle
      size={size}
      shape={shape}
      src={src}
      icon={icon}
      initials={initials}
      color={color}
      variant={variant}
      className={className}
      sx={sx}
    >
      {children}
    </IconStyle>
  );
};

// PropTypes for the Avatar component
Icon.propTypes = {
  /** Size of the avatar */
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  /** Shape of the avatar (circle or square) */
  shape: PropTypes.oneOf(["circle", "square"]),
  /** Image source for the avatar */
  src: PropTypes.string,
  /** Initials to display if no image is provided */
  initials: PropTypes.string,
  /** Color variant for the avatar background */
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
  ]),
  /** Variant of the avatar (filled, outlined, or contained) */
  variant: PropTypes.oneOf(["filled", "outlined", "contained"]),
  /** Additional CSS class name for custom styling */
  className: PropTypes.string,
  /** Custom styles using sx prop */
  sx: PropTypes.object,
};

// Default props for Avatar component
Icon.defaultProps = {
  size: "md",
  shape: "circle",
  src: "", // Default to an empty string, optional for initials
  initials: "", // Default initials if no src is provided
  color:"" ,
  variant: "",
  sx: {},
};
